import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { ClientPortalTimesheetEntry } from "../../../interfaces/ClientPortal";

const MONTHS_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const C = { navy: "#1b2559", gray: "#707eae", soft: "#a3aed0", track: "#f4f7fe", green: "#05cd99", brand: "#1466E3" };

const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 9, paddingTop: 26, paddingHorizontal: 28, paddingBottom: 30, color: C.navy },
  header: { backgroundColor: C.navy, borderRadius: 12, padding: 16, color: "#fff", marginBottom: 14 },
  brand: { fontSize: 8, letterSpacing: 1, color: "#8DDDFB" },
  hTitle: { fontSize: 16, fontFamily: "Helvetica-Bold", marginTop: 3 },
  hSub: { fontSize: 9, color: "#C4DBFD", marginTop: 2 },
  kpiRow: { flexDirection: "row", marginBottom: 14 },
  kpi: { flex: 1, backgroundColor: C.track, borderRadius: 10, padding: 12, marginRight: 8 },
  kpiLabel: { fontSize: 8, color: C.gray },
  kpiValue: { fontSize: 18, fontFamily: "Helvetica-Bold", marginTop: 3 },
  thRow: { flexDirection: "row", backgroundColor: C.track, borderRadius: 6, paddingVertical: 5, paddingHorizontal: 6, marginBottom: 2 },
  th: { fontSize: 7.5, color: C.gray, fontFamily: "Helvetica-Bold" },
  tr: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: "#eef0f4", paddingVertical: 4, paddingHorizontal: 6 },
  cDay: { width: 34, fontSize: 8.5 },
  cEmp: { width: 96, fontSize: 8.5, fontFamily: "Helvetica-Bold", paddingRight: 4 },
  cProj: { width: 96, fontSize: 8, color: C.gray, paddingRight: 4 },
  cAct: { flex: 1, fontSize: 8, paddingRight: 4 },
  cHours: { width: 40, fontSize: 8.5, textAlign: "right", fontFamily: "Helvetica-Bold" },
  chartBox: { backgroundColor: C.track, borderRadius: 10, padding: 10, marginBottom: 12 },
  chartTitle: { fontSize: 8, color: C.gray, fontFamily: "Helvetica-Bold", marginBottom: 6 },
  chartRow: { flexDirection: "row", alignItems: "flex-end", height: 74 },
  chartCol: { flex: 1, alignItems: "center", justifyContent: "flex-end" },
  chartVal: { fontSize: 6.5, color: C.navy, marginBottom: 2 },
  chartBar: { width: 11, backgroundColor: C.brand, borderTopLeftRadius: 2, borderTopRightRadius: 2 },
  chartLbl: { fontSize: 6.5, color: C.gray, marginTop: 3 },
  monthBar: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", backgroundColor: C.navy, borderRadius: 6, paddingVertical: 4, paddingHorizontal: 8, marginBottom: 3 },
  monthName: { fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold" },
  monthTotal: { fontSize: 7.5, color: "#C4DBFD" },
  empty: { backgroundColor: C.track, borderRadius: 8, padding: 12 },
  footer: { position: "absolute", bottom: 16, left: 28, right: 28, fontSize: 7, color: C.soft, textAlign: "center" },
});

const fmtH = (h: number) => `${(h ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}h`;
const dd = (d: number) => String(d).padStart(2, "0");

export interface TimesheetDetailPdfData {
  clientName: string;
  year: number;
  /** Meses que entraram no relatório, 1-12. Um item = mês; vários = período; doze = ano. */
  months: number[];
  /** Horas de TODOS os 12 meses do ano, para o gráfico do topo mostrar o contexto. */
  byMonth: { month: number; hours: number }[];
  entries: ClientPortalTimesheetEntry[];
}

// Soma decimal acumula erro de ponto flutuante; arredonda uma vez, no fim.
const soma = (ns: number[]) => Math.round(ns.reduce((s, n) => s + (n ?? 0), 0) * 100) / 100;

const TimesheetDetailPdf: React.FC<{ data: TimesheetDetailPdfData }> = ({ data }) => {
  const total = soma(data.entries.map((e) => e.hours));
  // Dia se repete entre meses, então o par mês-dia é o que conta como dia com apontamento.
  const days = new Set(data.entries.map((e) => `${e.month}-${e.day}`)).size;
  const periodLabel =
    data.months.length === 1
      ? `${MONTHS_FULL[data.months[0] - 1]} / ${data.year}`
      : data.months.length >= 12
      ? `Ano de ${data.year}`
      : `${data.months.length} meses de ${data.year}`;

  const maxBar = Math.max(1, ...data.byMonth.map((m) => m.hours));
  const selecionado = new Set(data.months);

  // Agrupa por mês para o relatório de período não virar uma lista corrida sem contexto.
  const grupos = data.months
    .slice()
    .sort((a, b) => a - b)
    .map((m) => ({
      month: m,
      linhas: data.entries
        .filter((e) => e.month === m)
        .sort((a, b) => a.day - b.day),
    }))
    .filter((g) => g.linhas.length > 0);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Text style={styles.brand}>DATAVALUE · NINA</Text>
          <Text style={styles.hTitle}>{data.clientName}</Text>
          <Text style={styles.hSub}>Lançamentos de horas · {periodLabel}</Text>
        </View>

        {/* Gráfico do ano no topo: barras desenhadas com View, sem dependência de canvas ou imagem. */}
        <View style={styles.chartBox}>
          <Text style={styles.chartTitle}>Horas por mês · {data.year}</Text>
          <View style={styles.chartRow}>
            {data.byMonth.map((m) => (
              <View key={m.month} style={styles.chartCol}>
                <Text style={[styles.chartVal, !selecionado.has(m.month) && { color: C.soft }]}>
                  {m.hours > 0 ? m.hours.toLocaleString("pt-BR", { maximumFractionDigits: 1 }) : ""}
                </Text>
                <View
                  style={[
                    styles.chartBar,
                    { height: Math.max(1, (m.hours / maxBar) * 54) },
                    !selecionado.has(m.month) && { backgroundColor: "#dfe3ec" },
                  ]}
                />
                <Text style={styles.chartLbl}>{MONTHS_SHORT[m.month - 1]}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.kpiRow}>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Total no período</Text>
            <Text style={[styles.kpiValue, { color: C.brand }]}>{fmtH(total)}</Text>
          </View>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Lançamentos</Text>
            <Text style={styles.kpiValue}>{data.entries.length}</Text>
          </View>
          <View style={[styles.kpi, { marginRight: 0 }]}>
            <Text style={styles.kpiLabel}>Dias com apontamento</Text>
            <Text style={styles.kpiValue}>{days}</Text>
          </View>
        </View>

        {grupos.length === 0 ? (
          <View style={styles.empty}>
            <Text style={{ fontSize: 9, color: C.gray }}>Nenhum lançamento de horas no período.</Text>
          </View>
        ) : (
          grupos.map((g) => (
            <View key={g.month} style={{ marginBottom: 10 }}>
              {/* Cabeçalho do mês só aparece quando há mais de um, para o PDF do mês seguir igual ao de antes. */}
              {grupos.length > 1 && (
                <View style={styles.monthBar}>
                  <Text style={styles.monthName}>{MONTHS_FULL[g.month - 1]}</Text>
                  <Text style={styles.monthTotal}>
                    {fmtH(soma(g.linhas.map((e) => e.hours)))} · {g.linhas.length} lançamentos
                  </Text>
                </View>
              )}
              <View style={styles.thRow}>
                <Text style={[styles.th, styles.cDay]}>Dia</Text>
                <Text style={[styles.th, styles.cEmp]}>Colaborador</Text>
                <Text style={[styles.th, styles.cProj]}>Projeto</Text>
                <Text style={[styles.th, styles.cAct]}>Atividade</Text>
                <Text style={[styles.th, styles.cHours]}>Horas</Text>
              </View>
              {g.linhas.map((e, i) => (
                <View key={i} style={styles.tr} wrap={false}>
                  <Text style={styles.cDay}>{dd(e.day)}</Text>
                  <Text style={styles.cEmp}>{e.employeeName}</Text>
                  <Text style={styles.cProj}>{e.projectName}</Text>
                  <Text style={styles.cAct}>{e.activity || "—"}</Text>
                  <Text style={styles.cHours}>{fmtH(e.hours)}</Text>
                </View>
              ))}
            </View>
          ))
        )}

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default TimesheetDetailPdf;
